import { z } from 'zod'

import { isString } from '@reward-platform/tsutil'
import { ZODException } from '@reward-platform/shared-api/exceptions/ZODException'
import { sanitizeBaseUrl } from '@reward-platform/utils'

import { Auth0ClientOptions } from '@auth0/nextjs-auth0/types'
import { IncomingMessage } from 'http'
import {
  getOpCoFromRequest,
  getPlatformBasePathFromRequest,
  getPlatformBaseUrlFromRequest,
  AVIOS_PLATFORM_COOKIES,
} from '@reward-platform/platform-config/server'
import logger from '@reward-platform/logger/edge'
import { getAuthConfigForOpCo } from '../../config'
import { AUTH_PATHS } from '../../utils/authPaths'
import { AUTH_COOKIES } from '../../utils'
import { handleCallback } from './auth0.hooks'

const COMMA_SEPARATOR = ','
const SPACE_SEPARATOR = ' '

const Auth0ParametersSchema = z.object({
  secret: z.string(),
  appBaseUrl: z.string(),
  domain: z.preprocess((value: unknown) => {
    if (isString(value)) {
      return sanitizeBaseUrl(value)
    }
    return value
  }, z.string()),
  clientId: z.string(),
  clientSecret: z.string(),
  noContentProfileResponseWhenUnauthenticated: z.boolean().default(true),
  authorizationParameters: z.object({
    audience: z.string().min(1),
    redirect_uri: z.string(),
    scope: z.preprocess(
      (value: unknown) =>
        isString(value) ? value.split(COMMA_SEPARATOR).join(SPACE_SEPARATOR) : value,
      z.string()
    ),
  }),
  cacheLocation: z.union([z.literal('memory'), z.literal('localstorage')]).default('memory'),
})

export const getOpCoAuth0Config = (req: IncomingMessage) => {
  const iataCode = getOpCoFromRequest(req)
  const opCoAuthConfig = getAuthConfigForOpCo(iataCode)
  const appBaseUrl = getPlatformBaseUrlFromRequest(req, false)
  const redirectUri = `${getPlatformBaseUrlFromRequest(req)}${AUTH_PATHS.CALLBACK}`
  const rawConfig: Auth0ClientOptions = {
    secret: opCoAuthConfig.clientSecret,
    appBaseUrl,
    domain: opCoAuthConfig.issuer,
    clientId: opCoAuthConfig.clientId,
    clientSecret: opCoAuthConfig.clientSecret,
    noContentProfileResponseWhenUnauthenticated: true,
    logoutStrategy: 'v2',
    authorizationParameters: {
      audience: opCoAuthConfig.audience,
      scope: opCoAuthConfig.scope,
      redirect_uri: redirectUri,
    },
    session: {
      cookie: {
        name: AUTH_COOKIES.AUTH0_SESSION_COOKIE_NAME,
      },
    },
  }

  const parsedConfig = Auth0ParametersSchema.safeParse(rawConfig)

  const basePath = getPlatformBasePathFromRequest(req)
  const completeConfig: Auth0ClientOptions = {
    ...parsedConfig.data,
    routes: {
      callback: '/auth/callback',
      login: '/auth/login',
      logout: '/auth/logout',
    },
    async onCallback(error, context) {
      const response = await handleCallback({
        error,
        context,
        appBaseUrl,
        errorRedirectPath: basePath ?? '/',
      })

      // Prolong the AVIOS_OPCO cookie so it doesn't expire before the session
      if (!error) {
        response.cookies.set({
          name: AVIOS_PLATFORM_COOKIES.AVIOS_OP_CO,
          value: iataCode,
          path: '/',
          maxAge: 30 * 24 * 60 * 60,
          sameSite: 'lax',
        })
      }

      return response
    },
  }

  if (!parsedConfig.success) {
    logger.error(
      `Auth0 configuration for OpCo ${iataCode} is invalid: ${JSON.stringify(
        parsedConfig.error.issues
      )}`
    )
    throw new ZODException(parsedConfig.error)
  }

  return { iataCode, config: completeConfig }
}
